import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Story from "./pages/company/Story";
import Difference from "./pages/company/Difference";
import Mission from "./pages/company/Mission";
import Values from "./pages/company/Values";
import Leadership from "./pages/company/Leadership";
import Partners from "./pages/company/Partners";
import Faq from "./pages/company/Faq";
import Technology from "./pages/Technology";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Careers from "./pages/Careers";
import News from "./pages/News";
import Contact from "./pages/Contact";
import Legal from "./pages/Legal";
import Sitemap from "./pages/Sitemap";
import NotFound from "./pages/NotFound";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: false };
    }
    static getDerivedStateFromError() {
        return { error: true };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-center">
                    <div>
                        <p className="eyebrow mb-4">Error</p>
                        <p className="h2-section">Something went wrong.</p>
                        <Link to="/" className="btn-primary mt-8">Back to home</Link>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function SmoothScroll() {
    useEffect(() => {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) return;
        const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);
    return null;
}

function ScrollManager() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                return;
            }
        }
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

export default function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <SmoothScroll />
                <ScrollManager />
                <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-blue focus:px-4 focus:py-2 focus:text-white">
                    Skip to content
                </a>
                <Navbar />
                <main id="main">
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/company/story" element={<Story />} />
                        <Route path="/company/difference" element={<Difference />} />
                        <Route path="/company/mission" element={<Mission />} />
                        <Route path="/company/values" element={<Values />} />
                        <Route path="/company/leadership" element={<Leadership />} />
                        <Route path="/company/partners" element={<Partners />} />
                        <Route path="/company/faq" element={<Faq />} />
                        <Route path="/technology" element={<Technology />} />
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/projects/:slug" element={<ProjectDetail />} />
                        <Route path="/careers" element={<Careers />} />
                        <Route path="/news" element={<News />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/privacy" element={<Legal kind="privacy" />} />
                        <Route path="/cookies" element={<Legal kind="cookies" />} />
                        <Route path="/terms" element={<Legal kind="terms" />} />
                        <Route path="/sitemap" element={<Sitemap />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </main>
                <Footer />
                <Toaster position="bottom-right" richColors toastOptions={{ style: { borderRadius: 0 } }} />
            </BrowserRouter>
        </ErrorBoundary>
    );
}
